import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, ArrowLeft, Check, Package, ClipboardList, QrCode } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { db, ensureDbOpen } from '../db/database';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { station, node } = useAuth();

  useEffect(() => {
    if (!station) {
      navigate('/stations');
      return;
    }
    if (!node) {
      navigate('/nodes');
      return;
    }
    loadProducts();
  }, [station, node, navigate]);

  const loadProducts = async () => {
    try {
      await ensureDbOpen();

      let loadedProducts = [];

      if (db.productsByStations) {
        // Nuevo esquema: productos por estación usando productsByStations
        // Aquí node.id corresponde al stationId que se guardó al sincronizar
        console.log('[NodeSpace] Cargando productos desde productsByStations para stationId (node.id):', node.id);
        const rows = await db.productsByStations
          .where('stationId')
          .equals(node.id)
          .toArray();

        console.log('[NodeSpace] Contexto de productos (space/station-node):', {
          spaceId: station?.id,
          stationNodeId: node?.id,
          stationNodeName: node?.name,
          rowsCount: rows.length,
        });

        const activeRows = rows.filter((p) => p.active === 1 || p.active === true);
        loadedProducts = activeRows.map((p) => ({
          id: p.productId,
          name: p.name,
          price: parseFloat(p.price) || parseFloat(p.basePrice) || 0,
          category: p.category || 'General',
          active: p.active,
        }));
      } else {
        // Esquema legacy: productos por nodeId en db.products
        console.log('[NodeSpace] Cargando productos desde products (legacy) para nodeId:', node.id);
        const localProducts = await db.products
          .where('nodeId')
          .equals(node.id)
          .toArray();
        loadedProducts = localProducts.filter((p) => p.active === 1 || p.active === true);
      }

      console.log('[NodeSpace] Productos cargados:', {
        stationId: node.id,
        nodeId: node.id,
        total: loadedProducts.length,
        categorias: [...new Set(loadedProducts.map((p) => p.category || 'General'))],
      });

      setProducts(loadedProducts);
      const uniqueCategories = [
        ...new Set(loadedProducts.map((p) => p.category || 'General')),
      ];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error loading products:', error);
      // En caso de error, deja la lista vacía pero no llama a API
      setProducts([]);
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleProduct = (product) => {
    const existingIndex = cart.findIndex(item => item.id === product.id);
    
    if (existingIndex >= 0) {
      const newCart = cart.filter(item => item.id !== product.id);
      setCart(newCart);
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const updateQuantity = (productId, change) => {
    setCart(cart.map(item => {
      if (item.id === productId) {
        const newQuantity = Math.max(1, item.quantity + change);
        return { ...item, quantity: newQuantity };
      }
      return item;
    }));
  };

  const isInCart = (productId) => {
    return cart.some(item => item.id === productId);
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const cartTotalUsd = getCartTotal();
  const bcvRateRaw = typeof window !== 'undefined' ? window.localStorage.getItem('bcvRate') : null;
  const bcvRate = bcvRateRaw ? parseFloat(bcvRateRaw) : null;
  const cartTotalBs = bcvRate && !Number.isNaN(bcvRate) && bcvRate > 0
    ? cartTotalUsd * bcvRate
    : null;

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredProducts = !normalizedSearch
    ? products
    : products.filter((p) => p.name.toLowerCase().includes(normalizedSearch));

  const handleContinue = () => {
    if (cart.length > 0) {
      navigate('/confirm', { state: { cart } });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Cargando productos...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pb-32">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 p-4 sticky top-0 z-10 shadow-lg">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => navigate('/nodes')}
            className="p-2 bg-white/20 rounded-lg"
          >
            <ArrowLeft size={24} className="text-white" />
          </button>
          <div className="text-center flex-1">
            <h1 className="text-xl font-bold text-white">{node?.name}</h1>
            <p className="text-sm text-white/80">{station?.name}</p>
          </div>
          <button
            onClick={() => navigate('/account-scanner')}
            className="p-2 bg-white/20 rounded-lg relative"
            title="Entregas por cuenta (QR)"
          >
            <QrCode size={24} className="text-white" />
          </button>
          <button
            onClick={() => navigate('/orders')}
            className="p-2 bg-white/20 rounded-lg relative"
            title="Ver órdenes locales"
          >
            <ClipboardList size={24} className="text-white" />
          </button>
        </div>
        <div className="mt-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field w-full"
            placeholder="Buscar producto..."
          />
        </div>
      </div>

      {/* Products Grid */}
      <div className="p-4">
        <div className="grid grid-cols-2 gap-3">
          {filteredProducts.map(product => (
            <button
              key={product.id}
              onClick={() => toggleProduct(product)}
              className={`product-card relative ${
                isInCart(product.id) ? 'product-card-selected' : ''
              }`}
            >
              {isInCart(product.id) && (
                <div className="absolute top-2 right-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                  <Check size={16} className="text-white" />
                </div>
              )}
              
              <div className="w-full h-20 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 rounded-lg mb-3 flex items-center justify-center">
                <Package size={28} className="text-primary-400" />
              </div>
              
              <h3 className="text-white font-semibold text-sm mb-1 text-left">
                {product.name}
              </h3>
              <p className="text-primary-400 font-bold text-lg text-left">
                ${(Number(product.price) || 0).toFixed(2)}
              </p>
              
              {isInCart(product.id) && (
                <div className="mt-3 flex items-center justify-between bg-gray-700 rounded-lg p-2"
                     onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => updateQuantity(product.id, -1)}
                    className="w-8 h-8 bg-gray-600 rounded text-white font-bold"
                  >
                    -
                  </button>
                  <span className="text-white font-semibold">
                    {cart.find(item => item.id === product.id)?.quantity || 1}
                  </span>
                  <button
                    onClick={() => updateQuantity(product.id, 1)}
                    className="w-8 h-8 bg-primary-600 rounded text-white font-bold"
                  >
                    +
                  </button>
                </div>
              )}
            </button>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="card p-8 text-center mt-8">
            <Package size={48} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No hay productos en esta categoría</p>
          </div>
        )}
      </div>

      {/* Cart Summary - Fixed Bottom */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 p-4 shadow-2xl">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <ShoppingCart size={24} className="text-primary-400" />
                  <span className="text-white font-semibold">
                    {cart.length} producto{cart.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {bcvRate && cartTotalBs && (
                  <div className="text-xs text-gray-400 border-l border-gray-700 pl-3">
                    <span className="uppercase tracking-wide text-[10px]">BCV:</span>{' '}
                    <span className="font-semibold text-white">{bcvRate}</span>
                  </div>
                )}
              </div>
              <div className="text-right">
                <p className="text-gray-400 text-sm">Total</p>
                <p className="text-2xl font-bold text-white">
                  ${cartTotalUsd.toFixed(2)}
                </p>
                {cartTotalBs && (
                  <p className="text-sm text-lime-300 font-semibold mt-1">
                    Bs {cartTotalBs.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleContinue}
              className="btn-primary w-full"
            >
              Continuar al Resumen
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
